import {cpServiceV2Get} from 'api';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';
import {sendSentryException} from 'utils/error-handling';
import dayjs from 'utils/dayjs';
import {
    ReviewTagsResults,
    RawReviewTag,
    RawReviewTagsResponse,
    RawReviewTagsResults,
    ReviewTagRecord,
} from 'api/facilities/review-tags.types';
import {type Period} from 'pages/facilities/driver-feedback/review-tags/ReviewTags.types';

/**
 * Formats the request parameters for getting review tags based on a selected period.
 *
 * @group DTO Formatters
 */
export const formatRequest = (
    selectedPeriod: Period,
    reservationType: string[]
) => {
    const toDate: string = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
    const fromDate: string = dayjs()
        .subtract(
            parseInt(String(selectedPeriod.value), 10) - 1,
            selectedPeriod.unit as dayjs.ManipulateType
        )
        .format('YYYY-MM-DD');

    return convertKeysToSnakeCase({
        fromDate,
        toDate,
        ...(reservationType.length ? {reservationTypes: reservationType} : {}),
    });
};

/**
 * Formats the response from the review tags API.
 * @group DTO Formatters
 */
export const formatResponse = (
    response: RawReviewTagsResponse
): ReviewTagsResults => {
    const formattedResponse: RawReviewTagsResults = camelizeKeys(response);

    return {
        ...formattedResponse,
        tags: formattedResponse.tags
            ? Object.entries(formattedResponse.tags)
                  .map(
                      ([label, data]: [
                          string,
                          RawReviewTag,
                      ]): ReviewTagRecord => ({
                          label,
                          ...data,
                      })
                  )
                  .sort((a, b) => b.count - a.count)
            : [],
    };
};

/**
 * Gets a facility's review tags within a date range.
 *
 * @see '/control-panel-service/v2/facilities/{facilityId}/reviews/tags'
 */
const getReviewTags = async (
    facilityId: number,
    selectedPeriod: Period,
    reservationType: string[]
): Promise<ReviewTagsResults> => {
    try {
        const params = formatRequest(selectedPeriod, reservationType);
        const response = await cpServiceV2Get<RawReviewTagsResponse>(
            `facilities/${facilityId}/reviews/tags`,
            params,
            // `reservation_types` is a repeated param, not an indexed array
            {paramsSerializer: {indexes: null}}
        );
        const formattedResponse = formatResponse(response.data.data);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);
        throw error;
    }
};

export default getReviewTags;
